import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import Header from '@/components/Header';
import { useTeam } from '@/contexts/TeamContext';
import {
  CalendarBlank, Clock, WarningCircle, Fire, Lightning, User,
  SpinnerGap, DownloadSimple, FunnelSimple
} from '@phosphor-icons/react';
import { format, parseISO, isValid } from 'date-fns';

const API = `${window.location.origin}/api`;

const URGENCY_CONFIG = {
  overdue: { label: 'Overdue', cls: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500', icon: Fire },
  due_today: { label: 'Due Today', cls: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500', icon: WarningCircle },
  due_soon: { label: 'Due in 3 Days', cls: 'bg-orange-50 text-orange-700 border-orange-200', dot: 'bg-orange-500', icon: Clock },
  this_week: { label: 'This Week', cls: 'bg-blue-50 text-blue-700 border-blue-200', dot: 'bg-blue-500', icon: CalendarBlank },
  upcoming: { label: 'Upcoming', cls: 'bg-slate-50 text-slate-600 border-slate-200', dot: 'bg-slate-400', icon: CalendarBlank },
};

function DeadlineCard({ task, index }) {
  const urgency = URGENCY_CONFIG[task.urgency] || URGENCY_CONFIG.upcoming;
  const UrgencyIcon = urgency.icon;

  const formatDate = (d) => {
    if (!d) return '';
    try { const p = parseISO(d); return isValid(p) ? format(p, 'MMM d, yyyy') : ''; }
    catch { return ''; }
  };

  const approvalBadge = task.approval_status === 'pending' ? 'bg-amber-50 text-amber-700 border-amber-200'
    : task.approval_status === 'approved' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : task.approval_status === 'rejected' ? 'bg-red-50 text-red-600 border-red-200' : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04 }}
      className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
      data-testid={`deadline-${task.task_id}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${urgency.dot}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="text-sm font-medium text-slate-900 truncate">{task.title}</h3>
            <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-medium border ${urgency.cls}`}>
              <UrgencyIcon size={10} weight="bold" className="inline mr-0.5" />
              {urgency.label}
            </span>
            {approvalBadge && (
              <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-medium border ${approvalBadge}`}>
                {task.approval_status}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <span className="flex items-center gap-1">
              <CalendarBlank size={12} /> {formatDate(task.due_date)}
            </span>
            {task.priority && (
              <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium border ${
                task.priority === 'high' ? 'bg-red-50 text-red-600 border-red-200' :
                task.priority === 'medium' ? 'bg-blue-50 text-blue-600 border-blue-200' :
                'bg-slate-50 text-slate-600 border-slate-200'
              }`}>{task.priority}</span>
            )}
            {task.assignee_name && (
              <span className="flex items-center gap-1">
                {task.assignee_picture ? (
                  <img src={task.assignee_picture} alt="" className="w-4 h-4 rounded-full" />
                ) : (
                  <User size={12} />
                )}
                {task.assignee_name}
              </span>
            )}
            {task.days_remaining !== undefined && (
              <span className={task.days_remaining < 0 ? 'text-red-600 font-medium' : ''}>
                {task.days_remaining < 0 ? `${Math.abs(Math.round(task.days_remaining))}d overdue` : `${Math.round(task.days_remaining)}d left`}
              </span>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function TeamDeadlines({ user }) {
  const { activeTeam } = useTeam();
  const [deadlines, setDeadlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [exporting, setExporting] = useState(false);

  const teamId = activeTeam?.team_id;

  const fetchDeadlines = useCallback(async () => {
    if (!teamId) return;
    try {
      const res = await axios.get(`${API}/teams/${teamId}/deadlines?days=60`, { withCredentials: true });
      setDeadlines(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [teamId]);

  useEffect(() => { fetchDeadlines(); }, [fetchDeadlines]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/teams/${teamId}/export/csv`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${activeTeam.team_name.replace(/\s/g, '_')}_tasks.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { console.error(err); }
    finally { setExporting(false); }
  };

  const filtered = filter === 'all' ? deadlines : deadlines.filter(d => d.urgency === filter);

  const counts = {
    overdue: deadlines.filter(d => d.urgency === 'overdue').length,
    due_today: deadlines.filter(d => d.urgency === 'due_today').length,
    due_soon: deadlines.filter(d => d.urgency === 'due_soon').length,
    this_week: deadlines.filter(d => d.urgency === 'this_week').length,
  };

  const canExport = activeTeam?.my_role === 'owner' || activeTeam?.my_role === 'admin';

  if (!activeTeam) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header user={user} />
        <main className="max-w-2xl mx-auto p-8 text-center pt-16">
          <p className="text-slate-500">Switch to a team workspace to view deadlines.</p>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header user={user} />
      <main className="max-w-4xl mx-auto p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight text-slate-900 flex items-center gap-3">
              <Lightning size={28} weight="duotone" className="text-navy" />
              Deadline Dashboard
            </h1>
            <p className="text-sm text-slate-500 mt-1">Upcoming deadlines for {activeTeam.team_name}</p>
          </div>
          {canExport && (
            <button onClick={handleExport} disabled={exporting}
              className="bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 font-medium px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors"
              data-testid="export-csv-btn">
              {exporting ? <SpinnerGap size={16} className="animate-spin" /> : <DownloadSimple size={16} weight="bold" />}
              Export CSV
            </button>
          )}
        </div>

        {/* Urgency summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { key: 'overdue', label: 'Overdue', color: 'bg-red-500', textColor: 'text-red-700', bgColor: 'bg-red-50 border-red-200' },
            { key: 'due_today', label: 'Due Today', color: 'bg-amber-500', textColor: 'text-amber-700', bgColor: 'bg-amber-50 border-amber-200' },
            { key: 'due_soon', label: 'Due Soon', color: 'bg-orange-500', textColor: 'text-orange-700', bgColor: 'bg-orange-50 border-orange-200' },
            { key: 'this_week', label: 'This Week', color: 'bg-blue-500', textColor: 'text-blue-700', bgColor: 'bg-blue-50 border-blue-200' },
          ].map(c => (
            <button key={c.key} onClick={() => setFilter(filter === c.key ? 'all' : c.key)}
              className={`border rounded-lg p-3 text-left transition-all ${filter === c.key ? c.bgColor : 'bg-white border-slate-200 hover:bg-slate-50'}`}
              data-testid={`filter-${c.key}`}>
              <div className="flex items-center gap-2 mb-1">
                <div className={`w-2.5 h-2.5 rounded-full ${c.color}`} />
                <span className="text-xs font-medium text-slate-600">{c.label}</span>
              </div>
              <p className={`text-2xl font-heading font-bold ${counts[c.key] > 0 ? c.textColor : 'text-slate-300'}`}>
                {counts[c.key]}
              </p>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><SpinnerGap size={28} className="text-navy animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-white border border-slate-200 rounded-lg">
            <CalendarBlank size={36} className="text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">
              {filter !== 'all' ? 'No tasks match this filter.' : 'No upcoming deadlines. Nice work!'}
            </p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="deadline-list">
            {filtered.map((t, i) => <DeadlineCard key={t.task_id} task={t} index={i} />)}
          </div>
        )}
      </main>
    </div>
  );
}

export default TeamDeadlines;
